import { updateTradingBalanceAccountState } from "@src/features/trade/hyperliquid/services/hyperliquidService"
import {
  getHyperliquidAccountConnectionStatus,
  getHyperliquidCandleConnectionStatus,
  getHyperliquidConnectionStatus,
  getHyperliquidMidsConnectionStatus,
  subscribeHyperliquidAccountConnectionStatus,
  subscribeHyperliquidAccountState,
  subscribeHyperliquidCandleConnectionStatus,
  subscribeHyperliquidConnectionStatus,
  subscribeHyperliquidMids,
  subscribeHyperliquidMidsConnectionStatus,
  type CandleInterval,
} from "@src/features/trade/hyperliquid/services/hyperliquidWebSocket"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useSyncExternalStore } from "react"
import { MAIN_DEX } from "../marketIdentity"
import { hyperliquidQueryKeys } from "../queries"
import type { HLDexName, HLTradingBalance } from "../types"

type DexSubscriptionInput = HLDexName | readonly HLDexName[]
const NO_DEX_SUBSCRIPTIONS = "__none__"

function isTradingBalanceResult(
  current: unknown
): current is { ok: true; data: HLTradingBalance } {
  return (
    typeof current === "object" &&
    current != null &&
    "ok" in current &&
    current.ok === true &&
    "data" in current &&
    typeof current.data === "object" &&
    current.data != null
  )
}

export function useHyperliquidMarketSubscription(
  address?: `0x${string}`,
  dex: HLDexName = MAIN_DEX
) {
  useHyperliquidMidsSubscription()
  useHyperliquidAccountStateSubscription(address, dex)
}

export function useHyperliquidMidsSubscription() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribeMids = subscribeHyperliquidMids((mids) => {
      queryClient.setQueryData(hyperliquidQueryKeys.mids(), {
        ok: true,
        data: mids,
      })
    })

    return () => {
      unsubscribeMids()
    }
  }, [queryClient])
}

export function useHyperliquidConnectionStatus() {
  return useSyncExternalStore(
    (notify) =>
      subscribeHyperliquidConnectionStatus(() => {
        notify()
      }),
    getHyperliquidConnectionStatus,
    () => false
  )
}

export function useHyperliquidMidsConnectionStatus(dex?: HLDexName) {
  const subscribe = useCallback(
    (notify: () => void) =>
      subscribeHyperliquidMidsConnectionStatus(dex, notify),
    [dex]
  )
  const getSnapshot = useCallback(
    () => getHyperliquidMidsConnectionStatus(dex),
    [dex]
  )

  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => false
  )
}

export function useHyperliquidCandleConnectionStatus(
  coin: string,
  interval: CandleInterval
) {
  const subscribe = useCallback(
    (notify: () => void) =>
      subscribeHyperliquidCandleConnectionStatus(coin, interval, notify),
    [coin, interval]
  )
  const getSnapshot = useCallback(
    () => getHyperliquidCandleConnectionStatus(coin, interval),
    [coin, interval]
  )

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

export function useHyperliquidAccountConnectionStatus(
  address: `0x${string}` | undefined,
  dex: HLDexName = MAIN_DEX
) {
  const subscribe = useCallback(
    (notify: () => void) =>
      address
        ? subscribeHyperliquidAccountConnectionStatus(address, dex, notify)
        : () => {},
    [address, dex]
  )
  const getSnapshot = useCallback(
    () =>
      address
        ? getHyperliquidAccountConnectionStatus(address, dex)
        : false,
    [address, dex]
  )

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

export function useHyperliquidAccountStateSubscription(
  address?: `0x${string}`,
  dexes: DexSubscriptionInput = MAIN_DEX
) {
  const queryClient = useQueryClient()
  const dexKey = normalizeDexSubscriptionKey(dexes)

  useEffect(() => {
    const normalizedDexes = parseDexSubscriptionKey(dexKey)
    const unsubscribeAccounts: Array<() => void> = []

    if (address) {
      for (const dex of normalizedDexes) {
        unsubscribeAccounts.push(
          subscribeHyperliquidAccountState(
            address,
            (state) => {
              queryClient.setQueryData(
                hyperliquidQueryKeys.account(address, dex),
                {
                  ok: true,
                  data: state,
                }
              )
              queryClient.setQueryData(
                hyperliquidQueryKeys.tradingBalance(address, dex),
                (current: unknown) => updateSelectedDexBalance(current, state)
              )

              if (dex === MAIN_DEX) {
                for (const subscribedDex of normalizedDexes) {
                  if (subscribedDex === MAIN_DEX) continue
                  queryClient.setQueryData(
                    hyperliquidQueryKeys.tradingBalance(address, subscribedDex),
                    (current: unknown) =>
                      updateMainPerpsAvailability(current, state)
                  )
                }
              }
            },
            dex
          )
        )
      }
    }

    return () => {
      for (const unsubscribeAccount of unsubscribeAccounts) {
        unsubscribeAccount()
      }
    }
  }, [address, dexKey, queryClient])
}

function normalizeDexSubscriptionKey(dexes: DexSubscriptionInput): string {
  const inputDexes = Array.isArray(dexes) ? dexes : [dexes]
  if (inputDexes.length === 0) return NO_DEX_SUBSCRIPTIONS
  return Array.from(new Set(inputDexes)).sort().join("|")
}

function parseDexSubscriptionKey(dexKey: string): HLDexName[] {
  if (dexKey === NO_DEX_SUBSCRIPTIONS) return []
  return dexKey.split("|") as HLDexName[]
}

function updateSelectedDexBalance(
  current: unknown,
  state: Parameters<typeof updateTradingBalanceAccountState>[1]
) {
  if (!isTradingBalanceResult(current)) {
    return current
  }
  return {
    ...current,
    data: updateTradingBalanceAccountState(current.data, state),
  }
}

function updateMainPerpsAvailability(
  current: unknown,
  mainState: Parameters<typeof updateTradingBalanceAccountState>[1]
) {
  if (
    !isTradingBalanceResult(current) ||
    current.data.availableSource !== "mainPerps"
  ) {
    return current
  }
  return {
    ...current,
    data: {
      ...current.data,
      available: mainState.withdrawable,
    },
  }
}
